/**
 * 极简 Toast · 右上角气泡，3 秒后自动消失
 * 用法：
 *   <ToastProvider> 包 App
 *   const { showToast } = useToast(); showToast({ message: 'xxx', type: 'error' })
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

type ToastType = 'info' | 'success' | 'error';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastCtx {
  showToast: (opts: { message: string; type?: ToastType }) => void;
}

const Ctx = createContext<ToastCtx | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useToast(): ToastCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

// 模块级 bridge：给 React 树之外的调用者（queryClient 的 onError 等）用
type ToastFn = ToastCtx['showToast'];
let _bridgeToast: ToastFn | null = null;

/**
 * 从 React 树之外发一个 toast。
 * ToastProvider 未挂载前调用会静默忽略（不抛），避免启动期意外崩溃。
 */
// eslint-disable-next-line react-refresh/only-export-components
export function toastBridge(opts: { message: string; type?: ToastType }) {
  _bridgeToast?.(opts);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((opts: { message: string; type?: ToastType }) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message: opts.message, type: opts.type ?? 'info' }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3000);
  }, []);

  // 把 showToast 挂到模块级 bridge，供非 React 代码使用
  useEffect(() => {
    _bridgeToast = showToast;
    return () => {
      if (_bridgeToast === showToast) _bridgeToast = null;
    };
  }, [showToast]);

  return (
    <Ctx.Provider value={{ showToast }}>
      {children}
      <div
        className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className="px-4 py-2.5 rounded-xl text-sm shadow-lg pointer-events-auto"
            style={{
              background:
                t.type === 'error' ? 'rgba(232,116,94,0.95)' :
                t.type === 'success' ? 'rgba(107,158,107,0.95)' :
                'rgba(60,70,80,0.95)',
              color: '#fff',
              minWidth: '200px',
              maxWidth: '400px',
              backdropFilter: 'blur(8px)',
            }}
          >
            {t.message}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}
